// A short list for engines without `Intl.supportedValuesOf` (nothing modern; kept for safety).
const FALLBACK_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Vienna',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Perth',
  'Pacific/Auckland',
];

// ICU still reports these legacy ids as canonical; show and send the current IANA names instead.
const MODERN_NAMES: Record<string, string> = {
  'America/Godthab': 'America/Nuuk',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** `Asia/Calcutta` → `Asia/Kolkata` when the engine accepts the modern name; otherwise unchanged. */
export function modernZoneName(zone: string): string {
  const modern = MODERN_NAMES[zone];
  return modern && isValidTimeZone(modern) ? modern : zone;
}

let cached: string[] | undefined;

/** IANA zone names known to this engine (`Intl.supportedValuesOf('timeZone')`), computed lazily. */
export function listTimeZones(): string[] {
  if (!cached) {
    let zones: string[] = FALLBACK_ZONES;
    try {
      const supported = (
        Intl as { supportedValuesOf?: (key: string) => string[] }
      ).supportedValuesOf?.('timeZone');
      if (supported && supported.length > 0) zones = supported;
    } catch {
      // keep the fallback
    }
    cached = [...new Set(['UTC', ...zones.map(modernZoneName)])].sort();
  }
  return cached;
}

/** The browser's zone, `UTC` when it cannot be determined. */
export function guessTimeZone(): string {
  try {
    return modernZoneName(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  } catch {
    return 'UTC';
  }
}

const normalise = (s: string): string => s.toLowerCase().replace(/[_/]/g, ' ');

/** Case-insensitive token search over zone names (`zur` → Europe/Zurich, `new york` → …). */
export function searchTimeZones(
  query: string,
  zones: readonly string[] = listTimeZones(),
  limit = 80
): string[] {
  const tokens = normalise(query).split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const zone of zones) {
    const hay = normalise(zone);
    if (tokens.every((t) => hay.includes(t))) {
      out.push(zone);
      if (out.length >= limit) break;
    }
  }
  return out;
}
